import React, { FC, ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { RemainTime, ResponsiveBlock } from '@web-kc/shared/components';
import { TenderTypes } from '@web-kc/core/api';
import { useRouter } from 'next/router';
import { useApp } from '@web-kc/core/providers';
import { useTranslation } from 'next-i18next';
import { LockFilled as LockIcon } from '@web-shared/core/icons';
import { formatTimestampWithMonth } from '@web-kc/shared/helpers';

interface Props {
  lot: TenderTypes['LotDto'];
}

export const LotItem: FC<Props> = ({ lot }) => {
  const theme = useTheme();
  const { locale = 'ru', pathname } = useRouter();
  const { t } = useTranslation(['common', 'cabinet']);
  const isKc = useApp() === 'kc';

  const isFavorites = pathname.startsWith('/cabinet/favorite');

  const isDraft =
    lot?.status?.id === 'DRAFT' || lot?.status?.id === 'ON_REVISION';

  const statusMap: { [key: string]: ReactNode } = {
    DRAFT: <Typography variant="body2">{lot?.status?.name}</Typography>,
    ON_REVISION: <Typography variant="body2">{lot?.status?.name}</Typography>,
    ON_MODERATION: <Typography variant="body2">{lot?.status?.name}</Typography>,
    ON_APPROVAL: <Typography variant="body2">{lot?.status?.name}</Typography>,
    ON_APPROVAL_UNSUCCESSFUL: (
      <Typography variant="body2">{lot?.status?.name}</Typography>
    ),
    UNSUCCESSFUL: (
      <Stack>
        <Typography variant="body2" color="error.main">
          {lot?.status?.name}
        </Typography>
        <Typography variant="body1">
          {formatTimestampWithMonth(lot?.committed)}
        </Typography>
      </Stack>
    ),
    SUCCESSFUL: (
      <Stack>
        <Typography variant="body2" color="success.main">
          {lot?.status?.name}
        </Typography>
        <Typography variant="body1">
          {formatTimestampWithMonth(lot?.committed)}
        </Typography>
      </Stack>
    ),
    OPEN_FOR_BID: (
      <RemainTime expires={lot?.expired} status={lot?.status?.name} />
    ),
  };

  const link = isDraft
    ? `/cabinet/tenders/edit/${lot.tender}?block=${lot.id}`
    : `/cabinet${isFavorites ? '/favorite-lots' : '/lots'}/${lot.id}`;

  const tenderLink = isDraft
    ? `/cabinet/tenders/edit/${lot.tender}`
    : `/cabinet${isFavorites ? '/favorite-tenders' : '/tenders'}/${lot.tender}`;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        paddingBottom: '10px',
        paddingTop: '10px',
        ':last-child': { borderBottom: 'none', paddingBottom: '0px' },
      }}
    >
      <ResponsiveBlock variant="desktop">
        <Box
          key={lot.id}
          sx={{
            borderBottom: `1px solid ${theme.palette.grey['300']}`,
            '&:last-of-type': { borderBottom: 'none' },
            pb: 2,
            pl: 1,
          }}
        >
          <Grid container>
            <Grid size={{ xs: 5.5 }}>
              <Box>
                <Typography
                  variant="body2"
                  color={isKc ? 'text.main' : 'primary.main'}
                  component={Link}
                  href={link}
                  sx={{
                    '&:hover': {
                      color: isKc ? 'primary.main' : 'primary.light',
                    },
                    maxWidth: '400px',
                  }}
                >
                  {lot.name?.[locale] ||
                    `${t('cabinet:myTenders.lot')} ${lot.id}`}{' '}
                  {!lot?.opened && <LockIcon />}
                </Typography>
                <Typography variant="body1" color="grey.500" mt="5px">
                  {lot?.location?.name}
                </Typography>
                {!!lot?.personDto?.name && (
                  <Typography variant="body1" color="grey.500" mt="5px">
                    {t('cabinet:myTenders.initiator')} — {lot?.personDto?.name}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 2 }} textAlign="start">
              <Box>{statusMap[`${lot.status?.id}`]}</Box>
            </Grid>
            <Grid size={{ xs: 4.5 }} textAlign="end">
              <Box>
                {!lot?.opened ? (
                  lot.type?.id === 'QUALIFICATION' ||
                  lot.type?.id === 'ZAKUP_IZ_1' ? (
                    <Typography variant="body2">{lot?.type?.name}</Typography>
                  ) : (
                    <Typography variant="body2">
                      {t('cabinet:myTenders.closedLot')}
                    </Typography>
                  )
                ) : lot.type?.id === 'QUALIFICATION' ? (
                  <Typography variant="body2">{lot?.type?.name}</Typography>
                ) : (
                  <Typography variant="body2">
                    {Number(lot?.amount).toLocaleString('ru-RU')}{' '}
                    {lot?.currency?.name}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {lot.status?.id === 'ON_MODERATION' && lot.comment && (
            <Box
              sx={{
                marginTop: '40px',
                padding: '2px 4px',
                backgroundColor: 'error.light',
                borderRadius: '2px',
                width: '60%',
              }}
            >
              <Typography color="error" variant="body1" fontWeight={600}>
                {t('cabinet:myTenders.rejectedByModerator')}{' '}
                {lot?.comment['comment'] || ''}
              </Typography>
            </Box>
          )}

          {lot.status?.id === 'ON_REVISION' && lot.moderation?.comment && (
            <Box
              sx={{
                marginTop: '12px',
                padding: '2px 10px',
                backgroundColor: 'error.light',
                borderRadius: '4px',
                width: '60%',
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
              }}
            >
              <Typography color="error" variant="body1">
                <Typography
                  component="span"
                  color="error"
                  variant="body1"
                  fontWeight={600}
                >
                  {t('cabinet:myTenders.rejectedByModerator')}
                </Typography>{' '}
                {lot.moderation.comment}
              </Typography>
            </Box>
          )}

          <Stack direction="row" alignItems="center" gap={1.25} mt={1.25}>
            <Chip label={`${t('cabinet:myTenders.lot')} ${lot.id}`} />
            <Chip
              label={`${t('cabinet:myTenders.tender')} ${lot.tender}`}
              component={Link}
              href={tenderLink}
              clickable
            />
            {isDraft && <Chip label={t('cabinet:myTenders.draft')} />}
            {!isDraft && (
              <Typography color="grey.500">
                {lot?.views} {t('cabinet:myTenders.viewings')}
              </Typography>
            )}
          </Stack>
        </Box>
      </ResponsiveBlock>

      <ResponsiveBlock variant="mobile">
        <ButtonBase
          key={lot.id}
          component={Link}
          href={link}
          sx={{ borderRadius: '4px', width: '100%' }}
          focusRipple
        >
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'white',
              borderRadius: '4px',
              padding: '20px',
              boxShadow: '0px 1px 3px 0px #0000004D',
            }}
          >
            <Stack direction="column" gap="20px">
              <Box textAlign="start">
                <Typography
                  variant="body2"
                  color={isKc ? 'text.main' : 'primary.main'}
                  sx={{
                    maxWidth: '400px',
                  }}
                >
                  {lot.name?.[locale] ||
                    `${t('cabinet:myTenders.lot')} ${lot.id}`}{' '}
                  {!lot?.opened && <LockIcon />}
                </Typography>
                <Typography variant="body1" color="grey.500" mt="5px">
                  {lot?.location?.name}
                </Typography>
                {!!lot?.personDto?.name && (
                  <Typography variant="body1" color="grey.500" mt="5px">
                    {t('cabinet:myTenders.initiator')} — {lot?.personDto?.name}
                  </Typography>
                )}
              </Box>
              <Box textAlign="start">
                {isDraft ? (
                  <Typography variant="body2">{lot?.status?.name}</Typography>
                ) : lot?.expired ? (
                  <RemainTime
                    expires={lot?.expired}
                    status={lot?.status?.name}
                  />
                ) : (
                  '-'
                )}
              </Box>
              <Box textAlign="start">
                {!lot?.opened ? (
                  <Typography variant="body2">{lot?.type?.name}</Typography>
                ) : lot.type?.id === 'QUALIFICATION' ? (
                  <Typography variant="body2">{lot?.type?.name}</Typography>
                ) : (
                  <>
                    <Typography variant="body1" color="grey.500">
                      {t('cabinet:favorites.volume')}
                    </Typography>
                    <Typography variant="body2">
                      {Number(lot?.amount).toLocaleString('ru-RU')}{' '}
                      {lot?.currency?.name}
                    </Typography>
                  </>
                )}
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={1.25}
              >
                <Chip label={`${t('cabinet:myTenders.lot')} ${lot.id}`} />
                <Chip
                  label={`${t('cabinet:myTenders.tender')} ${lot.tender}`}
                />
                {isDraft && <Chip label={t('cabinet:myTenders.draft')} />}
                {!isDraft && (
                  <Typography color="grey.500">
                    {lot?.views} {t('cabinet:myTenders.viewings')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        </ButtonBase>
      </ResponsiveBlock>
    </Box>
  );
};
